import { codeboxApi } from "api/codebox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Download01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { LockboxCredentialsCard } from "views/settings-integration/integrations/shared/LockboxCredentialsCard";
import { lockboxCredentialsState } from "views/settings-integration/integrations/shared/lockboxCredentialsState";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { ImportLockboxesModal } from "./components/ImportLockboxesModal";

type CodeboxTab = "Credentials";

const CODEBOX_TABS: Tab<CodeboxTab>[] = [
  { value: "Credentials", label: "Credentials" }
];

export const CodeboxView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();
  const queryClient = useQueryClient();

  // null until edited, so the saved username can be shown without a sync effect.
  const [editedUsername, setEditedUsername] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<CodeboxTab>("Credentials");
  const [importModalOpen, setImportModalOpen] = useState(false);

  const {
    data: status,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["CODEBOX", "STATUS"],
    queryFn: codeboxApi.getStatus
  });

  const username = editedUsername ?? status?.username ?? "";
  const credentialsState = lockboxCredentialsState({ isLoading, isError });

  const handleImportSuccess = useCallback(() => {
    setImportModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
    navigate("/settings/integration");
  }, [queryClient, navigate]);

  const { mutate: disconnectCodebox, isPending: isDisconnecting } = useMutation(
    {
      mutationKey: ["CODEBOX", "DELETE"],
      mutationFn: () => codeboxApi.disconnect(),
      onSuccess: () => {
        toast.success("Codebox disconnected successfully");
        queryClient.invalidateQueries({ queryKey: ["CODEBOX", "STATUS"] });
        refreshPartnerData();
        navigate("/settings/integration");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    }
  );

  const { mutate: updateCodebox, isPending } = useMutation({
    mutationKey: ["CODEBOX", "UPDATE"],
    mutationFn: () => codeboxApi.connect({ username, password }),
    onSuccess: () => {
      toast.success("Codebox credentials updated successfully");
      queryClient.invalidateQueries({ queryKey: ["CODEBOX", "STATUS"] });
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.CODEBOX}
          name="Codebox"
          path="/settings/integration/codebox"
          onDisconnect={() => disconnectCodebox()}
          isDisconnecting={isDisconnecting}
          actions={
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Download01Icon}
              onClick={() => setImportModalOpen(true)}
            >
              Import lockboxes
            </Button>
          }
        />
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => navigate("/settings/integration")}
            className="w-[125px]"
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => updateCodebox()}
            className="w-[125px]"
            disabled={!username || !password}
            loading={isPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <Tabs
        tabs={CODEBOX_TABS}
        active={activeTab}
        onChange={setActiveTab}
        tabStyle="line"
        size="md"
        fullWidthTabs={false}
      />
      <LockboxCredentialsCard
        idPrefix="codebox"
        state={credentialsState}
        username={username}
        onUsernameChange={setEditedUsername}
        password={password}
        onPasswordChange={setPassword}
      />
      <ImportLockboxesModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </Layout>
  );
};
